// src/authProvider.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const LOGIN_API_URL = `${API_URL}/auth/login`;

const authProvider = {
  // 页面加载时验证是否登录
  checkAuth: () => {
    const token = localStorage.getItem('token');
    if (!token) return Promise.reject();

    // Validate JWT expiry so expired tokens are caught immediately
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('roleId');
        localStorage.removeItem('username');
        localStorage.removeItem('mustChangePassword');
        return Promise.reject();
      }
    } catch {
      // Not a standard JWT — accept based on existence alone
    }

    return Promise.resolve();
  },

  // 处理请求错误
  checkError: (error) => {
    const status = error.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem('token'); // 清除 token
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('permissionSnapshot');
      localStorage.removeItem('mustChangePassword');
      // 清除所有菜单缓存
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('dynamicMenuCache')) {
          sessionStorage.removeItem(key);
        }
      });
      return Promise.reject(error); // RA 会自动跳转到 /login
    }
    return Promise.resolve();
  },

  login: async ({ username, password }) => {
    // 使用 Web Crypto 做 SHA-256 哈希（前提：后端接受并验证该哈希）
    const enc = new TextEncoder();
    const pwBuf = enc.encode(password);
    const hashBuf = await crypto.subtle.digest('SHA-256', pwBuf);
    const hashArray = Array.from(new Uint8Array(hashBuf));
    const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    // 立即清除明文密码引用
    password = undefined;

    // 检查是否为同一用户登录
    const previousUsername = localStorage.getItem('username');
    const isSameUser = previousUsername === username;

    // 只有在切换用户时才清除缓存，同一用户重复登录保留缓存
    if (!isSameUser) {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('dynamicMenuCache')) {
          sessionStorage.removeItem(key);
        }
      });
      localStorage.removeItem('permissionSnapshot');
      // eslint-disable-next-line no-console
      console.log('[Auth] 检测到用户切换，已清除菜单缓存');
    } else {
      // eslint-disable-next-line no-console
      console.log('[Auth] 同一用户登录，保留菜单缓存以加快加载速度');
    }

    return fetch(LOGIN_API_URL, {
      method: 'POST',
      body: JSON.stringify({ username, password: passwordHash }), // 注意字段名与后端约定
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        
        if (!res.ok) {
          // 根据状态码和后端返回的错误信息判断错误类型
          if (res.status === 401) {
            // 检查后端返回的具体错误信息
            const errorMsg = data.message || data.error || '';
            if (errorMsg.toLowerCase().includes('disabled') || errorMsg.toLowerCase().includes('inactive')) {
              throw new Error('ACCOUNT_DISABLED');
            } else if (errorMsg.toLowerCase().includes('password') || errorMsg.toLowerCase().includes('credentials')) {
              throw new Error('INVALID_CREDENTIALS');
            } else {
              throw new Error('INVALID_CREDENTIALS');
            }
          } else if (res.status === 403) {
            throw new Error('ACCOUNT_DISABLED');
          } else if (res.status >= 500) {
            throw new Error('SERVER_ERROR');
          } else {
            throw new Error('LOGIN_FAILED');
          }
        }
        
        return data;
      })
      .then((data) => {
        const { token, userId, id, user, username: returnedUsername, mustChangePassword } = data;
        // eslint-disable-next-line no-console
        console.log('Login response:', data);
        localStorage.setItem('token', token);
        localStorage.setItem('mustChangePassword', mustChangePassword ? 'true' : 'false');

        // 存储用户名（优先使用登录时输入的，其次使用后端返回的）
        localStorage.setItem('username', username || returnedUsername || '');
        
        // 兼容后端返回 userId、id 或 user.id
        if (userId) {
          localStorage.setItem('userId', String(userId));
        } else if (id) {
          localStorage.setItem('userId', String(id));
        } else if (user && user.id) {
          localStorage.setItem('userId', String(user.id));
        }

        // Always decode JWT to extract custom claims (userId fallback + roleId)
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));

          // userId fallback when it was not present in the response body
          if (!localStorage.getItem('userId')) {
            const extractedId =
              decoded.id ||
              decoded.userId ||
              decoded.sub ||
              decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
            if (extractedId) localStorage.setItem('userId', String(extractedId));
          }

          // roleId from the custom claim added by AuthController ("roleId" claim)
          const jwtRoleId = decoded.roleId;
          if (jwtRoleId !== undefined && jwtRoleId !== null && String(jwtRoleId) !== '') {
            localStorage.setItem('roleId', String(jwtRoleId));
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to decode JWT for claims:', e);
        }

        // eslint-disable-next-line no-console
        console.log('Stored userId:', localStorage.getItem('userId'), 'roleId:', localStorage.getItem('roleId'), 'username:', localStorage.getItem('username'));
        return Promise.resolve();
      });
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('roleId');
    localStorage.removeItem('username');
    localStorage.removeItem('permissionSnapshot');
    localStorage.removeItem('mustChangePassword');
    // 清除所有菜单缓存
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('dynamicMenuCache')) {
        sessionStorage.removeItem(key);
      }
    });
    return Promise.resolve();
  },

  getPermissions: () => Promise.resolve(),
};

export default authProvider;
